import React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Text,
  Box,
  TrashIcon,
  Link,
  Tooltip,
} from '@razorpay/blade/components';
import { format } from 'date-fns';
import { windowTypeNames } from '../constants';
import { OverridesTableProps, OverrideTableNode } from '../types';

const OverridesTable = ({
  overrides,
  isLoading = false,
  onDeleteOverride,
  onEmployeeCountClick,
}: OverridesTableProps) => {
  const getWindowTypeName = (settingName: string): string => {
    const windowType =
      settingName === 'declaration_window_config' ? 'declaration_window' : 'proof_upload_window';
    return windowTypeNames[windowType as keyof typeof windowTypeNames] || settingName;
  };

  const formatTimestamp = (timestamp: string | undefined): string => {
    if (!timestamp) return '-';

    const timestampNumber = parseInt(timestamp, 10);
    if (isNaN(timestampNumber) || timestampNumber <= 0) return '-';

    const isInSeconds = timestampNumber < 10000000000;
    const timestampMs = isInSeconds ? timestampNumber * 1000 : timestampNumber;
    const date = new Date(timestampMs);

    if (date.getFullYear() < 2000 || date.getFullYear() > 2100) {
      return '-';
    }

    return format(date, 'dd MMM yyyy');
  };

  const tableNodes: OverrideTableNode[] = overrides.map((override) => ({
    ...override,
    id: override.id || `override-${Math.random()}`,
    data: {
      window: getWindowTypeName(override.setting_name || ''),
      from: formatTimestamp(override.valid_from),
      to: formatTimestamp(override.valid_to),
      employeeCount: override.employee_count || 0,
    },
  }));

  return (
    <Box
      marginTop="spacing.7"
      borderColor="surface.border.gray.subtle"
      borderWidth="thin"
      borderRadius="medium"
      overflow="hidden"
      backgroundColor="surface.background.gray.moderate">
      <Table
        data={{ nodes: tableNodes }}
        isLoading={isLoading}
        gridTemplateColumns={tableNodes.length > 0 ? 'Repeat(5, 1fr) ' : undefined}
        backgroundColor="surface.background.gray.moderate">
        {(nodes) =>
          tableNodes.length > 0 ? (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Window
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      From
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      To
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Employees
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell textAlign="right">Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>

              <TableBody>
                {nodes.map((item) => {
                  return (
                    <TableRow key={item.id} item={item}>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.window}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.from}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.to}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Link
                          variant="button"
                          size="large"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEmployeeCountClick(item.id);
                          }}>
                          {String(item.data.employeeCount)}
                        </Link>
                      </TableCell>
                      <TableCell textAlign="right">
                        <Box display="flex" gap="spacing.3" justifyContent="flex-end">
                          <Tooltip content="Delete Override">
                            <Link
                              variant="button"
                              size="large"
                              color="negative"
                              icon={TrashIcon}
                              iconPosition="left"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteOverride(item.id);
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </>
          ) : (
            <Box padding="spacing.7" textAlign="center">
              <Text color="surface.text.gray.muted">No custom windows configured</Text>
            </Box>
          )
        }
      </Table>
    </Box>
  );
};

export default OverridesTable;
